"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { createClient } from "@/lib/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BottomNav } from "@/components/bottom-nav";
import {
  ArrowLeft,
  Loader2,
  Save,
  Building2,
  MapPin,
  Globe,
  Users,
  Phone,
  Mail,
  User,
} from "lucide-react";
import toast from "react-hot-toast";

interface ProfileData {
  name: string;
  phone: string;
  email: string;
}

interface CompanyData {
  id: string;
  name: string;
  abn: string;
  address: string;
  industry: string;
  size: string;
  website: string;
}

function normalizeWebsiteUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

export default function ContractorProfileEditPage() {
  const router = useRouter();
  const supabase = createClient();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState<ProfileData>({ name: "", phone: "", email: "" });
  const [company, setCompany] = useState<CompanyData>({ id: "", name: "", abn: "", address: "", industry: "", size: "", website: "" });

  useEffect(() => {
    async function load() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { router.push("/auth/login"); return; }

      const [{ data: profileData }, { data: companyData }] = await Promise.all([
        supabase.from("profiles").select("name, phone, email").eq("id", user.id).single(),
        supabase.from("companies").select("id, name, abn, address, industry, size, website").eq("profile_id", user.id).single(),
      ]);

      if (profileData) {
        setProfile({
          name: profileData.name || "",
          phone: profileData.phone || "",
          email: profileData.email || "",
        });
      }

      if (companyData) {
        setCompany({
          id: companyData.id,
          name: companyData.name || "",
          abn: companyData.abn || "",
          address: companyData.address || "",
          industry: companyData.industry || "",
          size: companyData.size || "",
          website: companyData.website || "",
        });
      }

      setLoading(false);
    }
    load();
  }, [supabase, router]);

  async function handleSave() {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get CSRF token
      const csrfRes = await fetch("/api/auth/csrf");
      const { token: csrfToken } = await csrfRes.json();

      // Update profile
      const { error: profileError } = await supabase
        .from("profiles")
        .update({
          name: profile.name.trim() || null,
          phone: profile.phone.trim() || null,
          email: profile.email.trim() || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (profileError) {
        toast.error(`Profile update failed: ${profileError.message}`);
        setSaving(false);
        return;
      }

      // Update company
      if (company.id) {
        const normalizedWebsite = normalizeWebsiteUrl(company.website);

        const { error: companyError } = await supabase
          .from("companies")
          .update({
            name: company.name.trim() || null,
            abn: company.abn.trim() || null,
            address: company.address.trim() || null,
            industry: company.industry.trim() || null,
            size: company.size.trim() || null,
            website: normalizedWebsite,
            updated_at: new Date().toISOString(),
          })
          .eq("id", company.id);

        if (companyError) {
          toast.error(`Company update failed: ${companyError.message}`);
          setSaving(false);
          return;
        }
      }

      toast.success("Profile updated!");
      router.push("/contractor/profile");
    } catch {
      toast.error("Something went wrong. Try again.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-56">
      <div className="mx-auto max-w-screen-sm px-4 py-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="icon" onClick={() => router.back()}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold">Edit Profile</h1>
        </div>

        {/* Personal Details */}
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <User className="h-5 w-5" />
              Your Details
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name" className="text-base">Full Name</Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  placeholder="Your full name"
                  className="h-12 text-base mt-1"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-base">Phone Number</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                    placeholder="0400 000 000"
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="email" className="text-base">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                    placeholder="you@example.com"
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Company Details */}
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 mb-4">
              <Building2 className="h-5 w-5" />
              Company Details
            </h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="companyName" className="text-base">Company Name</Label>
                <Input
                  id="companyName"
                  value={company.name}
                  onChange={(e) => setCompany({ ...company, name: e.target.value })}
                  placeholder="Company name"
                  className="h-12 text-base mt-1"
                />
              </div>
              <div>
                <Label htmlFor="abn" className="text-base">ABN</Label>
                <Input
                  id="abn"
                  value={company.abn}
                  onChange={(e) => setCompany({ ...company, abn: e.target.value })}
                  placeholder="00 000 000 000"
                  className="h-12 text-base mt-1"
                />
              </div>
              <div>
                <Label htmlFor="address" className="text-base">Address</Label>
                <div className="relative mt-1">
                  <MapPin className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="address"
                    value={company.address}
                    onChange={(e) => setCompany({ ...company, address: e.target.value })}
                    placeholder="Business address"
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="industry" className="text-base">Industry</Label>
                <Input
                  id="industry"
                  value={company.industry}
                  onChange={(e) => setCompany({ ...company, industry: e.target.value })}
                  placeholder="e.g. Residential Construction"
                  className="h-12 text-base mt-1"
                />
              </div>
              <div>
                <Label htmlFor="size" className="text-base">Company Size</Label>
                <div className="relative mt-1">
                  <Users className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="size"
                    value={company.size}
                    onChange={(e) => setCompany({ ...company, size: e.target.value })}
                    placeholder="e.g. 5-20 employees"
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="website" className="text-base">Website</Label>
                <div className="relative mt-1">
                  <Globe className="absolute left-3 top-3.5 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="website"
                    value={company.website}
                    onChange={(e) => setCompany({ ...company, website: e.target.value })}
                    placeholder="https://example.com"
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button — sticky at bottom */}
        <div className="fixed bottom-16 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur pb-[calc(env(safe-area-inset-bottom)+8px)] shadow-[0_-6px_20px_rgba(0,0,0,0.08)]">
          <div className="mx-auto max-w-screen-sm px-4 py-3">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full h-14 text-lg font-semibold"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="mr-2 h-5 w-5" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
      <BottomNav userType="contractor" />
    </div>
  );
}
